<script setup lang="ts">
import router from '@/router'
import { myRequest } from '@/utils/fetch'
import { loadStripe } from '@stripe/stripe-js'
import { useRoute } from 'vue-router'
import { ref } from 'vue'

const route = useRoute()
const renewing = route.query.renewing as string
const selectedProduct = ref<string | null>(null)
const billingInterval = ref('monthly')
const show = ref(true)

const pricingPlans = [
  {
    name: 'Lite',
    monthlyPrice: 4.99,
    yearlyPlan: 49.99,
    features: [
      '60 Individuals look ups',
      'AI discussion points',
      'Basic individual information',
      'Basic company information',
      'Recent company news',
      'Company financing information',
      'Basic analytics'
    ],
    buttonText: 'Start trial',
    recommended: false,
    active: true
  },
  {
    name: 'Premium',
    monthlyPrice: 6.99,
    yearlyPlan: 69.99,
    features: [
      '100 Individuals lookups',
      'AI discussion points',
      'Basic individual information',
      'Basic company information',
      'Recent company news',
      'Company financing information',
      'Basic analytics'
    ],
    buttonText: 'Start Trial',
    recommended: true,
    active: true
  },
  {
    name: 'Professional',
    monthlyPrice: 9.99,
    yearlyPlan: 99.99,
    features: [
      '200 Individuals lookups',
      'AI discussion points',
      'Basic individual information',
      'Basic company information',
      'Recent company news',
      'Company financing information',
      'Basic analytics'
    ],
    buttonText: 'Start Trial',
    recommended: false,
    active: true
  },
  {
    name: 'Team',
    monthlyPrice: 8.99,
    yearlyPlan: 89.99,
    features: ['100 Individuals look ups', 'All premium features', 'Advanced team analytics/insights'],
    buttonText: 'coming soon',
    active: false
  }
]

async function fetchClientSecret(plan: string) {
  const { client_secret: clientSecret, hasTrial } = await myRequest(
    'POST',
    `/payments/startTrial?renewing=${renewing}&plan=${plan}&billingInterval=${billingInterval.value}`
  )
  if (hasTrial) router.push('/onboarding/2')
  else return clientSecret
}

// Fetch Checkout Session and retrieve the client secret
async function initialize(plan: string) {
  selectedProduct.value = plan
  const clientSecret = await fetchClientSecret(plan)
  if (!clientSecret) return
  // Initialize Stripe.js
  const stripe = await loadStripe(
    'pk_live_51Qln8IAl4XukuuoVpabXEIy5LExREpqj8W45PoBuJbNOocQbudqKCiLKTfzPybet8zsFjTt2NjMONTM7l57B4iQc00dqovtIoR'
  )

  const checkout = await stripe?.initEmbeddedCheckout({ clientSecret })

  // Mount Checkout
  checkout?.mount('#checkout')
}
</script>

<template>
  <q-dialog v-model="show" persistent>
    <div v-if="selectedProduct === null" class="myCard q-pa-md" style="max-width: 1440px !important">
      <div class="text-h5 text-center">Choose your plan</div>
      <div class="text-center text-subtitle1 text-positive">
        <q-icon name="mdi-credit-card-off" size="sm" class="q-mr-xs" />
        No credit card required to start your trial
      </div>
      <div class="text-subtitle2 text-center q-mb-none">
        <q-icon name="mdi-calendar-clock" size="sm" class="q-mr-xs" />
        All plans include a 7-day free trial
      </div>
      <div class="q-pa-md">
        <div class="text-center">
          <q-btn-toggle
            v-model="billingInterval"
            :options="[
              { label: 'Monthly', value: 'monthly' },
              { label: 'Yearly', value: 'yearly' }
            ]"
            spread
            unelevated
            style="display: inline-block"
          />
        </div>

        <div class="row q-col-gutter-md full-width">
          <div
            v-for="plan in pricingPlans"
            :key="plan.name"
            class="col-12 col-sm-6 col-md paddingFixer3rd"
            style="min-width: 30%"
          >
            <q-badge v-if="plan.recommended" color="secondary" style="position: relative; top: 10px; left: 20px"
              >Recommended</q-badge
            >
            <div v-else style="height: 20px"></div>

            <div :class="{ recommended: plan.recommended }" class="q-pa-md planContainer full-height">
              <div>
                <div class="text-h6">
                  {{ plan.name }}
                </div>
                <div class="price q-my-md">
                  <span class="currency">$</span>
                  <span class="amount">{{ billingInterval === 'monthly' ? plan.monthlyPrice : plan.yearlyPlan }}</span>
                  <span class="period">/{{ billingInterval === 'monthly' ? 'month' : 'year' }}</span>
                </div>
                <q-btn
                  color="secondary"
                  class="full-width"
                  unelevated
                  :label="plan.buttonText"
                  @click="initialize(plan.name)"
                  :disabled="!plan.active"
                />
                <div>
                  <div class="q-mt-md">
                    <div v-for="feature in plan.features" :key="feature" class="row">
                      <div class="col-12 q-mb-md">
                        <q-icon name="mdi-check" color="positive" size="xs" class="q-mr-xs" />
                        <span class="text">{{ feature }}</span>
                      </div>
                    </div>
                  </div>
                </div>
              </div>
            </div>
          </div>
        </div>
      </div>
    </div>
    <div v-else class="center" id="checkout" style="max-width: 1440px !important"></div>
  </q-dialog>
</template>

<style scoped>
.price {
  font-size: 2rem;
  font-weight: 500;
}

.currency {
  font-size: 1.5rem;
  vertical-align: top;
}

.period {
  font-size: 1rem;
  color: #666;
}

.center {
  margin: 0;
  position: absolute;
  width: 80%;
  top: 50%;
  left: 50%;
  -ms-transform: translate(-50%, -50%);
  transform: translate(-50%, -50%);
}

.planContainer {
  border: 1px solid #9e9e9e;
  border-radius: 8px;
}

.recommended {
  border: 2px solid var(--q-secondary);
  border-radius: 8px;
}
</style>
